/**
 * DSTag — Design System Tag
 *
 * Matches Figma: Medarch-DS v.0.1 / Atoms / Tag
 * States: Default, Hover, Active
 */

import React from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COMPONENT, PRIMITIVE, px } from "../../tokens/design-tokens";

const tag = COMPONENT.tag;

interface DSTagProps {
  label: string;
  active?: boolean;
  icon?: React.ReactNode;
  onRemove?: () => void;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export function DSTag({
  label,
  active = false,
  icon,
  onRemove,
  onClick,
  sx,
}: DSTagProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: px(tag.gap),
        px: px(tag.padding.x),
        py: px(tag.padding.y),
        borderRadius: px(tag.radius),
        border: `1px solid ${active ? tag.outline.filter : tag.outline.default}`,
        backgroundColor: active ? tag.bg.active : tag.bg.default,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": onClick
          ? {
              backgroundColor: tag.bg.hover,
              borderColor: tag.outline.hover,
              "& .ds-tag-text": { color: tag.text.hover },
              "& .ds-tag-icon": { color: tag.icon.hover },
            }
          : {},
        ...sx,
      }}
    >
      {icon && (
        <Box
          className="ds-tag-icon"
          sx={{
            display: "flex",
            alignItems: "center",
            color: active ? tag.icon.active : tag.icon.default,
            "& svg": { fontSize: "16px" },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        className="ds-tag-text"
        sx={{
          fontFamily: PRIMITIVE.typography.fontFamily,
          fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
          fontWeight: PRIMITIVE.typography.fontWeight.regular,
          lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][12]),
          color: active ? tag.text.active : tag.text.default,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      {onRemove && (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: active ? tag.icon.active : tag.icon.default,
            "& svg": { fontSize: "14px" },
          }}
        >
          <CloseIcon />
        </Box>
      )}
    </Box>
  );
}
